"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { X, ImagePlus, ChevronDown, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useProviderStore } from "@/store/providerStore";
import { useInsuranceStore } from "@/store/insuranceStore";
import {
  ProviderDegree,
  ProviderGender,
  ProviderSession,
  ProviderPatientType,
} from "@/types";
import { useLanguage } from "@/context/LanguageContext";
import { ActivityLogger } from "@/lib/activityLog";
import {
  getImageUploadCopy,
  prepareImageUpload,
} from "@/lib/image-upload";

interface CreateProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
}

// Degrees/sessions/patients remain hardcoded — degrees is an enum on the
// Strapi schema, sessions and patients are short, stable lists with no
// admin-editable canonical content type. Specialty and language dropdowns
// are now driven by the provider-specialty / provider-language stores.
const DEGREE_OPTIONS: ProviderDegree[] = ["MD", "DO", "PhD", "PsyD", "LCSW", "LMFT", "LPC", "LCPC", "NP", "PA", "RN", "CRNA", "OTR/L", "COTA/L", "Other"];
const SESSION_OPTIONS: ProviderSession[] = ["In Person", "Virtual"];
const PATIENT_OPTIONS: ProviderPatientType[] = ["Adults", "Children", "Adolescents", "Elderly", "Couples", "Families"];

export default function CreateProviderModal({ isOpen, onClose, onResult }: CreateProviderModalProps) {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { createProvider, loading } = useProviderStore();
  const availableSpecialties = useProviderStore((s) => s.availableSpecialties);
  const availableLanguages = useProviderStore((s) => s.availableLanguages);
  const fetchSpecialties = useProviderStore((s) => s.fetchSpecialties);
  const fetchLanguages = useProviderStore((s) => s.fetchLanguages);
  const { insurances, fetchInsurances, createInsurancesFromList, deleteInsurance } = useInsuranceStore();

  const [providerName, setProviderName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [degrees, setDegrees] = useState<ProviderDegree[]>([]);
  const [yearsOfExperience, setYearsOfExperience] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [sessions, setSessions] = useState<ProviderSession[]>([]);
  const [acceptingPatients, setAcceptingPatients] = useState(true);
  const [waitlist, setWaitlist] = useState(false);
  const [gender, setGender] = useState<ProviderGender | null>(null);
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [patients, setPatients] = useState<ProviderPatientType[]>([]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bookingLink, setBookingLink] = useState("");
  const [sessionCost, setSessionCost] = useState("");
  const [officeHours, setOfficeHours] = useState("");
  const [officeAddress, setOfficeAddress] = useState("");
  const [insuranceInput, setInsuranceInput] = useState("");
  const [insurance, setInsurance] = useState<string[]>([]);
  const [showInsuranceSuggestions, setShowInsuranceSuggestions] = useState(false);
  const [deletingInsuranceId, setDeletingInsuranceId] = useState<string | null>(null);
  const [about, setAbout] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [showDegreeDropdown, setShowDegreeDropdown] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const insuranceInputRef = useRef<HTMLInputElement>(null);
  const uploadCopy = getImageUploadCopy(language, 'single');

  useEffect(() => {
    if (isOpen) {
      void Promise.all([fetchInsurances(), fetchSpecialties(), fetchLanguages()]);
    }
  }, [isOpen, fetchInsurances, fetchSpecialties, fetchLanguages]);

  const filteredInsuranceSuggestions = insurances.filter(
    (ins) =>
      ins.name.toLowerCase().includes(insuranceInput.toLowerCase()) &&
      !insurance.includes(ins.name)
  );

  if (!isOpen) return null;

  const isAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';

  const handleFile = async (file: File) => {
    const { file: prepared, error } = await prepareImageUpload(file, language);
    if (error) {
      onResult?.(false, error);
      return;
    }
    setImage(prepared);
    setImageName(prepared.name);
    setPreview(URL.createObjectURL(prepared));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) {
      void handleFile(e.dataTransfer.files[0]);
    }
  };

  const toggleLanguage = (lang: string) => {
    setLanguages((prev) => prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]);
  };

  const toggleSession = (session: ProviderSession) => {
    setSessions((prev) => prev.includes(session) ? prev.filter((s) => s !== session) : [...prev, session]);
  };

  const toggleSpecialty = (specialty: string) => {
    setSpecialties((prev) => {
      if (prev.includes(specialty)) return prev.filter((s) => s !== specialty);
      if (prev.length >= 5) return prev;
      return [...prev, specialty];
    });
  };

  const togglePatient = (patient: ProviderPatientType) => {
    setPatients((prev) => {
      if (prev.includes(patient)) return prev.filter((p) => p !== patient);
      if (prev.length >= 4) return prev;
      return [...prev, patient];
    });
  };

  const toggleDegree = (degree: ProviderDegree) => {
    setDegrees((prev) => {
      if (prev.includes(degree)) return prev.filter((d) => d !== degree);
      if (prev.length >= 4) return prev;
      return [...prev, degree];
    });
  };

  const isFormValid = () => providerName.trim() && degrees.length > 0 && yearsOfExperience && specialties.length > 0 && phone.trim();

  const handleSubmit = async () => {
    if (!currentUser || !isFormValid() || loading) return;

    // Save new insurances to the database
    if (insurance.length > 0) {
      await createInsurancesFromList(insurance);
    }

    const result = await createProvider({
      providerName: providerName.trim(),
      groupName: groupName.trim() || undefined,
      degrees: degrees[0],
      degreesList: degrees,
      yearsOfExperience: parseInt(yearsOfExperience, 10),
      languages, sessions, acceptingPatients, waitlist, specialties, patients,
      gender: gender ?? undefined,
      location: officeAddress.trim() || 'Not specified',
      phone: phone.trim(),
      email: email.trim() || undefined,
      bookingLink: bookingLink.trim() || undefined,
      sessionCost: sessionCost.trim() || undefined,
      officeHours: officeHours.trim() || undefined,
      officeAddress: officeAddress.trim() || undefined,
      insurance: insurance.length > 0 ? insurance : undefined,
      about: about.trim() || undefined,
    }, image);

    if (result.success) {
      onResult?.(true, t.modals.provider.providerCreatedSuccess);
      ActivityLogger.createProvider(currentUser, providerName.trim(), result.provider?.documentId);
      resetForm();
      onClose();
      if (result.provider?.documentId) {
        router.push(`/providers/${result.provider.documentId}`);
      }
    } else {
      onResult?.(false, result.error || t.modals.provider.failedToCreate);
    }
  };

  const resetForm = () => {
    setProviderName(""); setGroupName(""); setDegrees([]); setYearsOfExperience("");
    setLanguages([]); setSessions([]); setAcceptingPatients(true); setWaitlist(false); setGender(null);
    setSpecialties([]); setPatients([]); setPhone("");
    setEmail(""); setBookingLink(""); setSessionCost(""); setOfficeHours("");
    setOfficeAddress(""); setInsuranceInput(""); setInsurance([]); setShowInsuranceSuggestions(false);
    setAbout(""); setImage(null); setPreview(null); setImageName(null); setShowDegreeDropdown(false);
  };

  const inputClass = "w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary";
  const labelClass = "block text-sm font-medium text-foreground mb-1.5";

  const handleDeleteInsuranceSuggestion = async (documentId: string, insuranceName: string) => {
    if (!isAdmin || deletingInsuranceId) return;

    setDeletingInsuranceId(documentId);
    const success = await deleteInsurance(documentId);
    setDeletingInsuranceId(null);

    if (success) {
      setInsurance((prev) => prev.filter((name) => name !== insuranceName));
      onResult?.(true, 'Insurance option deleted');
    } else {
      onResult?.(false, 'Failed to delete insurance option');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="create-provider-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.modals.provider.createProvider}</h2>
            <p className="text-sm text-caption">{t.modals.provider.addNewProvider}</p>
          </div>
          <button data-testid="create-provider-close-btn" onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition" type="button" aria-label="Close">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className={labelClass}>{t.modals.provider.providerName} <span className="text-red-500">*</span></label>
            <input data-testid="create-provider-name-input" type="text" value={providerName} onChange={(e) => setProviderName(e.target.value)} placeholder={t.modals.provider.enterProviderName} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Group Name (Optional)</label>
            <input
              data-testid="create-provider-group-name-input"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group or practice name"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.uploadPhoto}</label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition ${preview ? "border-primary" : "border-border hover:border-primary/50"}`} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
              {preview ? (
                <div>
                  <div className="relative inline-block">
                    <img src={preview} alt="Preview" className="max-h-32 mx-auto rounded" />
                    <button onClick={() => { setImage(null); setPreview(null); setImageName(null); }} className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white" type="button"><X size={14} /></button>
                  </div>
                  {imageName && <p className="text-xs text-caption mt-2 truncate">{imageName}</p>}
                </div>
              ) : (
                <>
                  <ImagePlus size={32} className="mx-auto text-foreground-muted mb-2" />
                  <button data-testid="create-provider-choose-file-btn" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 border border-border rounded-md text-sm text-foreground bg-background hover:bg-sidebar-hover transition" type="button">{t.modals.provider.chooseFile}</button>
                  <p className="text-xs text-caption mt-2">{uploadCopy.uploadPrompt}</p>
                  <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
                </>
              )}
              <input
                data-testid="create-provider-photo-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    void handleFile(e.target.files[0]);
                  }
                }}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="create-provider-degrees-experience-row">
            <div className="relative">
              <label className={labelClass}>{t.modals.provider.degrees} <span className="text-red-500">*</span> <span className="text-caption">({t.modals.provider.max} 4)</span></label>
              <button data-testid="create-provider-degrees-dropdown-toggle" type="button" onClick={() => setShowDegreeDropdown(!showDegreeDropdown)} className={`${inputClass} flex items-center justify-between`}>
                <span className={degrees.length ? "text-foreground" : "text-foreground-muted"}>
                  {degrees.length ? degrees.join(", ") : t.modals.provider.selectDegree}
                </span>
                <ChevronDown size={16} className={`transition ${showDegreeDropdown ? "rotate-180" : ""}`} />
              </button>
              {showDegreeDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {DEGREE_OPTIONS.map((d) => (
                    <label key={d} data-testid={`create-provider-degree-option-${d}`} className="flex items-center gap-2 px-3 py-2 hover:bg-sidebar-hover cursor-pointer text-sm text-foreground">
                      <input type="checkbox" checked={degrees.includes(d)} onChange={() => toggleDegree(d)} disabled={!degrees.includes(d) && degrees.length >= 4} className="w-4 h-4 rounded" />
                      {d}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>{t.modals.provider.yearsOfExperience}</label>
              <input data-testid="create-provider-years-of-experience-input" type="number" value={yearsOfExperience} onChange={(e) => setYearsOfExperience(e.target.value)} placeholder={t.modals.provider.enterYears} min="0" className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="create-provider-languages-sessions-row">
            <div>
              <label className={labelClass}>{t.modals.provider.languages}</label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableLanguages
                  .filter((lang) => !lang.archived)
                  .map((lang) => {
                    const localized =
                      lang.name === "English" ? t.modals.provider.english :
                      lang.name === "Spanish" ? t.modals.provider.spanish :
                      lang.name;
                    return (
                      <label key={lang.documentId} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          checked={languages.includes(lang.name)}
                          onChange={() => toggleLanguage(lang.name)}
                          className="w-4 h-4 rounded border-border"
                        />
                        {localized}
                      </label>
                    );
                  })}
              </div>
            </div>
            <div>
              <label className={labelClass}>{t.modals.provider.sessions}</label>
              <div className="space-y-2">
                {SESSION_OPTIONS.map((session) => (
                  <label key={session} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input type="checkbox" checked={sessions.includes(session)} onChange={() => toggleSession(session)} className="w-4 h-4 rounded border-border" />
                    {session === "In Person" ? t.modals.provider.inPerson : t.modals.provider.virtual}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex border border-border rounded-md overflow-hidden">
            <button type="button" onClick={() => { setAcceptingPatients(true); setWaitlist(false); }} className={`flex-1 py-2 text-sm font-medium transition ${acceptingPatients && !waitlist ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-sidebar-hover"}`}>
              {t.modals.provider.acceptingNewPatients}
            </button>
            <button type="button" onClick={() => { setAcceptingPatients(false); setWaitlist(true); }} className={`flex-1 py-2 text-sm font-medium transition ${waitlist ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-sidebar-hover"}`}>
              {t.modals.provider.waitlist}
            </button>
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.gender}</label>
            <div className="flex border border-border rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => setGender(gender === 'male' ? null : 'male')}
                className={`flex-1 py-2 text-sm font-medium transition ${gender === 'male' ? 'bg-foreground text-background' : 'bg-background text-foreground hover:bg-sidebar-hover'}`}
              >
                {t.modals.provider.genderMale}
              </button>
              <button
                type="button"
                onClick={() => setGender(gender === 'female' ? null : 'female')}
                className={`flex-1 py-2 text-sm font-medium transition ${gender === 'female' ? 'bg-foreground text-background' : 'bg-background text-foreground hover:bg-sidebar-hover'}`}
              >
                {t.modals.provider.genderFemale}
              </button>
            </div>
          </div>

          <div className="relative">
            <label className={labelClass}>{t.modals.provider.mySpecialties} <span className="text-red-500">*</span> <span className="text-caption">({t.modals.provider.max} 5)</span></label>
            <button data-testid="create-provider-specialties-dropdown-toggle" type="button" onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)} className={`${inputClass} flex items-center justify-between`}>
              <span className={specialties.length ? "text-foreground" : "text-foreground-muted"}>
                {specialties.length ? specialties.join(", ") : t.modals.provider.selectSpecialties}
              </span>
              <ChevronDown size={16} className={`transition ${showSpecialtyDropdown ? "rotate-180" : ""}`} />
            </button>
            {showSpecialtyDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {availableSpecialties
                  .filter((s) => !s.archived)
                  .map((s) => (
                    <label key={s.documentId} data-testid={`create-provider-specialty-option-${s.name}`} className="flex items-center gap-2 px-3 py-2 hover:bg-sidebar-hover cursor-pointer text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={specialties.includes(s.name)}
                        onChange={() => toggleSpecialty(s.name)}
                        disabled={!specialties.includes(s.name) && specialties.length >= 5}
                        className="w-4 h-4 rounded"
                      />
                      {s.name}
                    </label>
                  ))}
              </div>
            )}
          </div>

          <div className="relative">
            <label className={labelClass}>{t.modals.provider.myPatients} <span className="text-caption">({t.modals.provider.max} 4)</span></label>
            <button data-testid="create-provider-patients-dropdown-toggle" type="button" onClick={() => setShowPatientDropdown(!showPatientDropdown)} className={`${inputClass} flex items-center justify-between`}>
              <span className={patients.length ? "text-foreground" : "text-foreground-muted"}>
                {patients.length ? patients.join(", ") : t.modals.provider.selectPatientTypes}
              </span>
              <ChevronDown size={16} className={`transition ${showPatientDropdown ? "rotate-180" : ""}`} />
            </button>
            {showPatientDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {PATIENT_OPTIONS.map((p) => (
                  <label key={p} data-testid={`create-provider-patient-option-${p}`} className="flex items-center gap-2 px-3 py-2 hover:bg-sidebar-hover cursor-pointer text-sm text-foreground">
                    <input type="checkbox" checked={patients.includes(p)} onChange={() => togglePatient(p)} disabled={!patients.includes(p) && patients.length >= 4} className="w-4 h-4 rounded" />
                    {p}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.phone} <span className="text-red-500">*</span></label>
            <input data-testid="create-provider-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.modals.provider.enterPhone} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.email}</label>
            <input data-testid="create-provider-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.modals.provider.enterEmail} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.websiteLinkUrl}</label>
            <input data-testid="create-provider-website-input" type="url" value={bookingLink} onChange={(e) => setBookingLink(e.target.value)} placeholder="https://..." className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.cost}</label>
            <p className="text-xs text-caption -mt-1 mb-1.5">{t.modals.provider.sessionCost}</p>
            <input
              data-testid="create-provider-session-cost-input"
              type="text"
              maxLength={100}
              value={sessionCost}
              onChange={(e) => setSessionCost(e.target.value)}
              placeholder={t.modals.provider.sessionCostPlaceholder}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.officeHours}</label>
            <textarea
              data-testid="create-provider-office-hours-textarea"
              value={officeHours}
              onChange={(e) => setOfficeHours(e.target.value)}
              placeholder={t.modals.provider.officeHoursPlaceholder}
              rows={2}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.officeAddress}</label>
            <textarea
              data-testid="create-provider-office-address-textarea"
              value={officeAddress}
              onChange={(e) => setOfficeAddress(e.target.value)}
              placeholder={t.modals.provider.enterFullAddress}
              rows={2}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.insuranceAccepted}</label>
            <div className="relative mb-2">
              <input
                ref={insuranceInputRef}
                type="text"
                value={insuranceInput}
                onChange={(e) => {
                  setInsuranceInput(e.target.value);
                  setShowInsuranceSuggestions(true);
                }}
                onFocus={() => setShowInsuranceSuggestions(true)}
                onBlur={() => setTimeout(() => setShowInsuranceSuggestions(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && insuranceInput.trim()) {
                    e.preventDefault();
                    if (!insurance.includes(insuranceInput.trim())) {
                      setInsurance([...insurance, insuranceInput.trim()]);
                    }
                    setInsuranceInput('');
                    setShowInsuranceSuggestions(false);
                  }
                }}
                placeholder={t.modals.provider.typeToSearch}
                className={inputClass}
              />
              {showInsuranceSuggestions && insuranceInput && filteredInsuranceSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filteredInsuranceSuggestions.map((ins) => (
                    <div key={ins.id} className="flex items-center border-b last:border-b-0 border-border/50">
                      <button
                        type="button"
                        className="flex-1 px-3 py-2 text-left text-sm text-foreground hover:bg-background-secondary"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!insurance.includes(ins.name)) {
                            setInsurance([...insurance, ins.name]);
                          }
                          setInsuranceInput('');
                          setShowInsuranceSuggestions(false);
                        }}
                      >
                        {ins.name}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          title="Delete insurance option"
                          aria-label={`Delete insurance option ${ins.name}`}
                          disabled={deletingInsuranceId === ins.documentId}
                          className="px-2 py-2 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleDeleteInsuranceSuggestion(ins.documentId, ins.name);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-caption mb-2">
              Tip: write the insurance name and press Enter to add it, or click a suggestion.
            </p>
            {insurance.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {insurance.map((ins) => (
                  <span key={ins} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-background-secondary text-foreground border border-border rounded">
                    {ins}
                    <button type="button" onClick={() => setInsurance(insurance.filter(i => i !== ins))} className="hover:text-red-500">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>{t.modals.provider.about}</label>
            <textarea
              data-testid="create-provider-bio-textarea"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder={t.modals.provider.aboutPlaceholder}
              rows={4}
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-border sticky bottom-0 bg-background">
          <button data-testid="create-provider-cancel-btn" type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-md text-sm text-foreground font-medium hover:bg-sidebar-hover transition">{t.common.cancel}</button>
          <button data-testid="create-provider-submit-btn" type="button" onClick={handleSubmit} disabled={!isFormValid() || loading} className="px-4 py-2 bg-foreground text-background rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? t.modals.provider.creating : t.modals.provider.createProviderBtn}
          </button>
        </div>
      </div>
    </div>
  );
}
